import { Component } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ActionButtonComponent } from 'libs/ui/src/lib/action-button/action-button.component';
import { GridLinkRolesDetailComponent } from 'libs/ui/src/lib/grid-link-roles-detail/grid-link-roles-detail.component';
import { ProductListDataGridService } from './product-list-data-grid.service';
import {
  customDecode,
  customEncode,
  customEncrypt,
  encrypt,
  ProjectsFacade,
  SharedFacade,
} from '@piqet-angular/data';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ViewportScroller } from '@angular/common';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { CustomModalService } from '@piqet-angular/shared';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription } from 'rxjs';
import { GridMyProjectImageComponent } from 'libs/ui/src/lib/grid-my-project-image/grid-my-project-image.component';

@Component({
  selector: 'piqet-angular-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
})
export class ProductListComponent {
  isScreenDisabled: any;
  columnDefs!: ColDef[];
  activatedRoute: any;
  activatedRouteProductId: any;
  frameworkComponents = {
    actionRenderer: ActionButtonComponent,
    linkRenderer: GridLinkRolesDetailComponent,
    GridMyProjectImageComponent: GridMyProjectImageComponent,
  };
  searchData: any = '';
  sortColumnState: any;
  someSubscription: any;
  gridName: string = 'Product list';
  event: any;
  isHideSideBar = true;
  headerData = {
    materialName: 'Products',
  };
  productData: any;
  deleteProductItem!: Subscription;
  copyProductData!: Subscription;
  constructor(
    private readonly productListDataGridService: ProductListDataGridService,
    public readonly sharedFacade: SharedFacade,
    public readonly projectFacade: ProjectsFacade,
    public readonly route: ActivatedRoute,
    private readonly projectsService: ProjectsService,
    private readonly viewportScroller: ViewportScroller,
    private readonly sharedService: SharedService,
    private readonly modal: CustomModalService,
    private readonly toaster: ToastrService,
    private readonly router: Router
  ) {
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    const url = location.href.split('/');
    this.activatedRouteProductId = Number(atob(decodeURIComponent(customDecode(url[6]))));
    this.columnDefs = this.productListDataGridService.productDataColumn(this.activatedRoute, true);
  }

  ngOnInit(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.triggerScroll();
    this.getProductList();
    this.deleteProductItem = this.productListDataGridService.deleteProductData$.subscribe(
      async (deleteData: any) => {
        this.deleteProduct(deleteData[0]);
      }
    );

    this.copyProductData = this.productListDataGridService.copyProductData$.subscribe(
      async (data: any) => {
        this.copyProduct(data);
      }
    );
  }

  copyProduct(data: any) {
    this.modal.invoke('copySubProduct', {
      initialState: {
        productData: data,
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveCopyProductEvent.subscribe((data: any) => {
      this.saveCopyProduct(data);
    });
  }

  saveCopyProduct(data: any) {
    this.projectsService.copySubProduct(data.payload).subscribe(res => {
      if (res.messageType) {
        this.toaster.error(res.message);
      } else {
        this.toaster.success(res.message);
        this.getProductList();
        this.modal.closeAll();
        this.sharedService.accessProductSideBarData(() => {});
      }
    });
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }

  async getProductList() {
    this.productData = (
      await this.projectsService.getProductContentBYProductConfigId(this.activatedRouteProductId)
    ).data;
    const hasMultipleScenarios = this.productData.length > 1;
    this.columnDefs = this.productListDataGridService.productDataColumn(
      this.activatedRoute,
      hasMultipleScenarios
    );
    this.projectFacade.storeProjectNameForBreadcrumb(this.productData.productName);
  }

  async addNewSubProduct() {
    const labelProduct = 'Make a product';
    let response = await this.projectsService.getMyAllProductProjectModalEditData(
      Number(this.activatedRouteProductId) ? this.activatedRouteProductId : 0,
      true,
      0
    );
    this.modal.invoke('newAddSubProduct', {
      initialState: {
        newMyProductProjects: response.data,
        pathName: this.activatedRoute,
        label: labelProduct,
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveNewMyProductProjectEvent.subscribe(
      (saveNewMyProductProjectEvent: any) => {
        this.addProductProject(saveNewMyProductProjectEvent);
      }
    );
  }

  addProductProject(data: any) {
    this.projectsService
      .saveSubProjectProduct(data)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getProductList();
          this.sharedService.accessProductSideBarData(() => {});
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
        if (!res.messageType) {
          this.sharedService.scenarioNameFunc(data?.name);
          localStorage.setItem('scenarioName', data?.name);
          const encryptedValue = customEncrypt(res.data);
          this.router.navigate(
            [
              `projects/product-configuration/${customEncode(
                btoa(this.activatedRouteProductId)
              )}/configurations`,
            ],
            {
              queryParams: {
                param: encryptedValue,
              },
            }
          );
        }
      });
  }

  editRecord(record: any) {
    this.projectFacade.inlineDataEditScenarioName(record[0], this.activatedRoute);
  }

  deleteProduct(data: any) {
    this.projectsService
      .deleteProductProject(data.productId)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getProductList();
          this.sharedService.accessProductSideBarData(() => {});
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  changeRowOrder(event: any) {
    if (event) {
      const formattedArray = event.map((item: any, index: number) => ({
        productId: item.productId,
        orderId: index + 1,
      }));
      this.projectsService.addUpdateProductOrder(formattedArray).subscribe(res => {
        if (res.messageType) {
          this.toaster.error(res.message);
        } else {
          this.toaster.success(res.message);
          this.sharedService.accessProductSideBarData(() => {});
        }
      });
    }
  }

  ngOnDestroy() {
    this.deleteProductItem.unsubscribe();
    this.copyProductData.unsubscribe();
  }
}
